import { apiClient } from '@/shared/lib/axios';

/** Params for admin order rezept sheet list. */
export interface OrderRezeptSheetListParams {
  site_type?: string[];
  service_type?: string;
  order_date_from?: string;
  order_date_to?: string;
  inc_delivered?: boolean;
  sort_by?: string;
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

/** One detail row within an order. */
export interface OrderRezeptSheetDetailItem {
  id: number;
  row_no: number;
  rezept_sheet_id: number;
  sheet_number: string;
  sheet_name: string;
  unit_amount: string | null;
  price: number;
  quantity: number;
  subtotal: number;
  delivery_fee: number;
  is_include_case: boolean;
  request_delivery_date: string | null;
  ship_date: string | null;
  remarks: string | null;
}

/** One order in the list. */
export interface OrderRezeptSheetListItem {
  id: number;
  clinic_id: number;
  clinic_number: string | null;
  clinic_name: string | null;
  site_type: string | null;
  service_type: string | null;
  order_no: number;
  order_date: string;
  subtotal: number;
  delivery_fee: number;
  total: number;
  details: OrderRezeptSheetDetailItem[];
  created_at: string | null;
  updated_at: string | null;
}

export interface OrderRezeptSheetListMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface OrderRezeptSheetListResponse {
  success: boolean;
  message?: string;
  data: {
    data: OrderRezeptSheetListItem[];
    meta: OrderRezeptSheetListMeta;
  };
}

/** Fetch paginated order rezept sheets. */
export const getOrderRezeptSheets = async (
  site: string,
  params: OrderRezeptSheetListParams
): Promise<OrderRezeptSheetListResponse> => {
  const response = await apiClient.get<OrderRezeptSheetListResponse>(
    `/admin/${site}/order-rezept-sheets`,
    { params }
  );
  return response.data;
};

/** Download order rezept sheets as CSV. */
export const downloadOrderRezeptSheetsCsv = async (
  site: string,
  params: OrderRezeptSheetListParams
): Promise<void> => {
  const { page: _page, per_page: _perPage, ...csvParams } = params;
  const response = await apiClient.get(`/admin/${site}/order-rezept-sheets/csv`, {
    params: csvParams,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=UTF-8;' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  a.download = `order_rezept_sheets${dateStr}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Payload for bulk ship_date update. */
export interface UpdateShipDatePayload {
  details: {
    id: number;
    ship_date: string | null;
    remarks: string | null;
  }[];
}

export interface UpdateShipDateResponse {
  success: boolean;
  message?: string;
  data: {
    updated_count: number;
  };
}

/** Bulk update ship_date and remarks. */
export const updateShipDates = async (
  site: string,
  payload: UpdateShipDatePayload
): Promise<UpdateShipDateResponse> => {
  const response = await apiClient.post<UpdateShipDateResponse>(
    `/admin/${site}/order-rezept-sheets/ship-date`,
    payload
  );
  return response.data;
};

// ── Detail / Edit ──

/** A rezept sheet option for the dropdown. */
export interface RezeptSheetOption {
  id: number;
  service_type: string | null;
  sheet_number: string;
  sheet_name: string;
  unit_amount: string | null;
  price: number;
}

/** Detail response shape (single order + rezept_sheets dropdown). */
export interface OrderRezeptSheetDetailData extends OrderRezeptSheetListItem {
  rezept_sheets: RezeptSheetOption[];
}

export interface OrderRezeptSheetDetailResponse {
  success: boolean;
  message?: string;
  data: OrderRezeptSheetDetailData;
}

/** Fetch a single order rezept sheet by ID. */
export const getOrderRezeptSheetDetail = async (
  site: string,
  id: number
): Promise<OrderRezeptSheetDetailResponse> => {
  const response = await apiClient.get<OrderRezeptSheetDetailResponse>(
    `/admin/${site}/order-rezept-sheets/${id}`
  );
  return response.data;
};

/** Detail row for update payload. */
export interface UpdateOrderRezeptSheetDetailPayload {
  id?: number | null;
  rezept_sheet_id?: number | null;
  quantity?: number | null;
  delivery_fee?: number | null;
  is_include_case?: boolean;
  request_delivery_date?: string | null;
  ship_date?: string | null;
  remarks?: string | null;
  is_delete?: boolean;
}

export interface UpdateOrderRezeptSheetPayload {
  details: UpdateOrderRezeptSheetDetailPayload[];
}

export interface UpdateOrderRezeptSheetResponse {
  success: boolean;
  message?: string;
  data: OrderRezeptSheetListItem;
}

/** Update an order rezept sheet (details add/update/delete). */
export const updateOrderRezeptSheet = async (
  site: string,
  id: number,
  payload: UpdateOrderRezeptSheetPayload
): Promise<UpdateOrderRezeptSheetResponse> => {
  const response = await apiClient.put<UpdateOrderRezeptSheetResponse>(
    `/admin/${site}/order-rezept-sheets/${id}`,
    payload
  );
  return response.data;
};
